import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { get } from "@/lib/api";

export const DIRS = [
  { key: "editorial", label: "A · Editorial Precision" },
  { key: "bold", label: "B · Bold Utility" },
  { key: "quiet", label: "C · Quiet Intelligence" },
  { key: "expressive", label: "D · Expressive Precision" },
];

export const GROUP_LABEL = { strong: "Strong", possible: "Possible", needs_information: "Needs info", low_priority: "Low priority", excluded: "Excluded" };
export const GROUP_ORDER = ["strong", "possible", "needs_information", "low_priority", "excluded"];
export const STATUS_LABEL = { queued: "In queue", pending_review: "Pending review", contacted: "Contacted", pre_screening: "Pre-screening", ready_for_first_visit: "Ready for first visit", unsuitable: "Unsuitable" };
export const READY_LABEL = { ready: "Ready for contact", restricted: "Restricted", blocked: "Blocked" };

const ACTIONS = [
  ["ready_for_first_visit", "Mark ready for first visit", "ready_for_first_visit"],
  ["move_to_prescreening", "Move to pre-screening", "pre_screening"],
  ["log_contact", "Record contact", "contacted"],
  ["mark_pending_review", "Mark pending review", "pending_review"],
  ["add_to_queue", "Add to queue", "queued"],
  ["create_followup", "Schedule follow-up", null],
  ["mark_unsuitable", "Mark unsuitable", "unsuitable"],
];

export const rel = (s) => {
  if (!s) return "Never";
  const d = Math.floor((Date.now() - new Date(s).getTime()) / 86400000);
  return d <= 0 ? "Today" : d === 1 ? "Yesterday" : d < 60 ? `${d}d ago` : `${Math.round(d / 30)}mo ago`;
};
export const fmt = (s) => (s ? new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");

export const actionsFor = (d) => {
  if (!d) return { primary: null, others: [] };
  const A = d.allowed_actions;
  const all = ACTIONS.map(([k, label, to]) => ({ key: k, label, to, ...A[k] }));
  let primary = all.slice(0, 5).find((a) => a.enabled) || null;
  if (d.outreach_blocks.length && d.relationship && !A.log_contact.enabled && primary?.key !== "move_to_prescreening") {
    primary = { key: "resolve", label: "Resolve restriction", enabled: true, to: null };
  }
  return { primary, others: all.filter((a) => a.key !== primary?.key) };
};

export const useConceptData = () => {
  const [sp] = useSearchParams();
  const studies = useQuery({ queryKey: ["studies"], queryFn: () => get("/studies") });
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard") });
  const studyId = sp.get("study") || studies.data?.find((s) => s.reference === "ALN-RSP-001")?.id;
  const wb = useQuery({ queryKey: ["workbench", studyId], queryFn: () => get(`/studies/${studyId}/workbench`), enabled: !!studyId });
  return { studies: studies.data, dash: dash.data, wb: wb.data, studyId };
};

export const useCandidate = (studyId, pid) =>
  useQuery({ queryKey: ["candidate", studyId, pid], queryFn: () => get(`/studies/${studyId}/candidates/${pid}`), enabled: !!(studyId && pid) });

export const useSimulated = () => {
  const [over, setOver] = useState({});
  const run = (pid, a) => {
    if (!a) return;
    if (a.to) setOver((o) => ({ ...o, [pid]: a.to }));
    toast(`Prototype: “${a.label}” previewed — not saved`, { description: "Concept routes never write to the demo database." });
  };
  return { over, run };
};

export const todayItems = (d, dir) => {
  if (!d) return [];
  const t = d.today;
  const link = (x, extra = "") => `/concepts/${dir}/workbench?study=${x.study_id}&p=${x.participant_id}${extra}`;
  const items = [
    ...d.followups_due.map((x) => ({ id: x.id, kind: x.due_date < t ? "Overdue follow-up" : "Follow-up due", urgency: x.due_date < t ? 0 : 1, title: x.title, who: x.participant_name, ref: x.participant_ref, study: x.study_ref, when: x.due_date < t ? `Overdue · ${fmt(x.due_date)}` : "Today", to: link(x), action: "Complete" })),
    ...d.pending_review.map((x) => ({ id: x.id, kind: STATUS_LABEL[x.status], urgency: 2, title: x.status === "queued" ? "Review queued candidate" : "Attempt first contact", who: x.participant_name, ref: x.participant_ref, study: x.study_ref, when: rel(x.updated_at), to: link(x), action: x.status === "queued" ? "Review" : "Contact" })),
    ...d.awaiting_review.map((x) => ({ id: `aw-${x.study_id}-${x.participant_id}`, kind: "Strong · ready", urgency: 3, title: "New strong candidate, contact-ready", who: x.participant_name, ref: x.participant_ref, study: x.study_ref, when: `Rank #${x.rank}`, to: link(x), action: "Add to queue" })),
    ...d.handoff.map((x) => ({ id: `h-${x.id}`, kind: STATUS_LABEL[x.status], urgency: 4, title: x.status === "pre_screening" ? "Complete pre-screening" : "Hand off to EDGE", who: x.participant_name, ref: x.participant_ref, study: x.study_ref, when: rel(x.updated_at), to: link(x), action: "Open" })),
  ];
  return items.sort((a, b) => a.urgency - b.urgency);
};

export const criteriaColumns = (rows) => (rows[0]?.evidence || []).map((e) => e.label);

export const filterRows = (rows, { group = "all", q = "" }) => {
  const s = q.trim().toLowerCase();
  return rows.filter((r) => (group === "all" || r.group === group) && (!s || r.name.toLowerCase().includes(s) || r.participant_ref.toLowerCase().includes(s)));
};

export const useVirtual = (count, rowH, ref, overscan = 10) => {
  const [st, setSt] = useState(0);
  const [h, setH] = useState(800);
  useEffect(() => {
    const el = ref.current;
    if (!el) { return undefined; }
    const on = () => setSt(el.scrollTop);
    const ro = new ResizeObserver(() => setH(el.clientHeight));
    ro.observe(el);
    el.addEventListener("scroll", on, { passive: true });
    on();
    return () => { el.removeEventListener("scroll", on); ro.disconnect(); };
  }, [ref, count]);
  const start = Math.max(0, Math.floor(st / rowH) - overscan);
  const end = Math.min(count, Math.ceil((st + h) / rowH) + overscan);
  const reveal = (i) => {
    const el = ref.current;
    if (!el) return;
    if (i * rowH < el.scrollTop) el.scrollTop = i * rowH;
    else if ((i + 1) * rowH > el.scrollTop + el.clientHeight) el.scrollTop = (i + 1) * rowH - el.clientHeight;
  };
  return { start, end, top: start * rowH, total: count * rowH, reveal };
};

export const useHotkeys = (map) => {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    const h = (e) => {
      const key = `${e.metaKey || e.ctrlKey ? "mod+" : ""}${e.key.toLowerCase()}`;
      if (key === "mod+k" && ref.current[key]) { e.preventDefault(); ref.current[key](e); return; }
      if (e.target.closest("input, textarea, select, [role=dialog]")) return;
      if (key === "enter" && e.target.closest("button, a, [role=radio], [role=tab], [role=option]")) return;
      if (ref.current[key]) { e.preventDefault(); ref.current[key](e); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
};

export const useSelection = (rows) => {
  const [sp, setSp] = useSearchParams();
  const want = sp.get("p");
  const pid = rows.some((r) => r.participant_id === want) ? want : rows[0]?.participant_id;
  const idx = Math.max(0, rows.findIndex((r) => r.participant_id === pid));
  const ref = useRef(idx);
  const seen = useRef(pid);
  if (seen.current === pid || !rows.some((r) => r.participant_id === seen.current)) { seen.current = pid; ref.current = idx; }
  const select = (id) => { seen.current = id; const n = new URLSearchParams(window.location.search); n.set("p", id); setSp(n, { replace: true }); };
  const move = (d) => {
    if (!rows.length) return 0;
    const n = Math.min(rows.length - 1, Math.max(0, ref.current + d));
    ref.current = n;
    seen.current = rows[n].participant_id;
    select(rows[n].participant_id);
    return n;
  };
  return { pid, idx, select, move, ref };
};

export const CommandPalette = ({ open, setOpen, dir, rows = [], studies = [], studyId, contentClass, itemClass, personTo }) => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const remote = useQuery({ queryKey: ["participants", { q }], queryFn: () => get("/participants", { q, page_size: 8 }), enabled: open && q.trim().length >= 2 });
  const local = useMemo(() => filterRows(rows, { q }).slice(0, 6), [rows, q]);
  const go = (to) => { setOpen(false); setQ(""); nav(to); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className={`overflow-hidden p-0 sm:max-w-xl ${contentClass}`} data-testid="concept-command-palette">
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <Command shouldFilter={false} className="bg-transparent text-inherit">
          <CommandInput value={q} onValueChange={setQ} placeholder="Search participants, studies, or jump to…" data-testid="concept-palette-input" />
          <CommandList className="max-h-[420px]">
            <CommandEmpty>No results.</CommandEmpty>
            {["today", "workbench"].some((s) => !q || s.includes(q.toLowerCase())) && (
            <CommandGroup heading="Go to">
              {["today", "workbench"].filter((s) => !q || s.includes(q.toLowerCase())).map((s) => (
                <CommandItem key={s} className={itemClass} onSelect={() => go(`/concepts/${dir}/${s}${studyId ? `?study=${studyId}` : ""}`)}>{s === "today" ? "Today" : "Study workbench"}</CommandItem>
              ))}
            </CommandGroup>
            )}
            {studies.some((s) => !q || `${s.reference} ${s.name}`.toLowerCase().includes(q.toLowerCase())) && (
            <CommandGroup heading="Studies">
              {studies.filter((s) => !q || `${s.reference} ${s.name}`.toLowerCase().includes(q.toLowerCase())).slice(0, 6).map((s) => (
                <CommandItem key={s.id} className={itemClass} onSelect={() => go(`/concepts/${dir}/workbench?study=${s.id}`)}>{s.reference} — {s.name}</CommandItem>
              ))}
            </CommandGroup>
            )}
            {local.length > 0 && (
              <CommandGroup heading="Candidates in this study">
                {local.map((r) => (
                  <CommandItem key={r.participant_id} className={itemClass} onSelect={() => go(`/concepts/${dir}/workbench?study=${studyId}&p=${r.participant_id}`)}>
                    {r.name} · {r.participant_ref} · {GROUP_LABEL[r.group]}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {remote.data?.rows?.length > 0 && (
              <CommandGroup heading={personTo ? "All participants" : "All participants (leaves prototype → real Participant 360)"}>
                {remote.data.rows.map((p) => (
                  <CommandItem key={p.id} className={itemClass} onSelect={() => go(personTo ? personTo(p) : `/participants/${p.id}`)}>{p.name} · {p.participant_ref} · {p.area}</CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export const ConceptBar = ({ dir, screen, tone = "light", screens = ["today", "workbench"] }) => {
  const [sp] = useSearchParams();
  const qs = sp.get("study") ? `?study=${sp.get("study")}` : "";
  const base = tone === "dark" ? "bg-white/10 text-white border-white/15" : "bg-black/80 text-white border-black/10";
  return (
    <div className={`fixed bottom-3 right-3 z-50 flex items-center gap-1 rounded-full border px-2 py-1.5 text-[11.5px] shadow-2xl backdrop-blur-xl ${base}`} data-testid="concept-switcher">
      <Link to="/concepts" className="rounded-full px-2.5 py-1 hover:bg-white/15" data-testid="concept-bar-gallery">Concepts</Link>
      <span className="opacity-30">|</span>
      {DIRS.map((d) => (
        <Link key={d.key} to={`/concepts/${d.key}/${d.key !== "expressive" && screen === "triage" ? "workbench" : screen}${qs}`} data-testid={`concept-bar-${d.key}`}
          className={`rounded-full px-2.5 py-1 ${d.key === dir ? "bg-white text-black" : "hover:bg-white/15"}`}>{d.label.split(" · ")[0]}</Link>
      ))}
      <span className="opacity-30">|</span>
      {screens.map((s) => (
        <Link key={s} to={`/concepts/${dir}/${s}${qs}`} data-testid={`concept-bar-${s}`}
          className={`rounded-full px-2.5 py-1 capitalize ${s === screen ? "bg-white text-black" : "hover:bg-white/15"}`}>{s}</Link>
      ))}
      <span className="hidden px-2 opacity-60 md:inline">Prototype · live read-only data · actions simulated</span>
    </div>
  );
};
